import { useState, useRef, useEffect } from 'react';

export interface UseDropdownOptions {
  closeOnOutsideClick?: boolean;
  closeOnEscape?: boolean;
  dataAttribute?: string;
}

export interface UseDropdownReturn {
  isOpen: boolean;
  buttonRef: React.RefObject<HTMLButtonElement | null>;
  open: () => void;
  close: () => void;
  toggle: () => void;
}

/**
 * Hook for managing dropdown state with click-outside and keyboard support
 */
export const useDropdown = (options: UseDropdownOptions = {}): UseDropdownReturn => {
  const {
    closeOnOutsideClick = true,
    closeOnEscape = true,
    dataAttribute = 'data-dropdown'
  } = options;

  const [isOpen, setIsOpen] = useState(false);
  const buttonRef = useRef<HTMLButtonElement>(null);

  const open = () => setIsOpen(true);
  const close = () => setIsOpen(false);
  const toggle = () => setIsOpen(prev => !prev);

  // Close dropdown when clicking outside
  useEffect(() => {
    if (!isOpen || !closeOnOutsideClick) return;
    
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      // Check if click is on the button or inside the dropdown
      if (buttonRef.current?.contains(target) || target.closest(`[${dataAttribute}]`)) {
        return;
      }
      close();
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen, closeOnOutsideClick, dataAttribute]);

  // Close dropdown on Escape key
  useEffect(() => {
    if (!isOpen || !closeOnEscape) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        close();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, closeOnEscape]);

  return {
    isOpen,
    buttonRef,
    open,
    close,
    toggle
  };
};